import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';

import useFormOptions from './useFormOptions';

const FORM_OPTIONS = {
  jobRoles: [{ label: 'Fleet Manager', value: 'fleet_manager' }],
  countries: [{ label: 'United States', value: 'US' }],
  states: { US: [{ label: 'Virginia', value: 'VA' }] },
  phoneCountryCodes: [{ label: '+1', value: 1 }],
  permissions: [{ id: 'report-user', label: 'Report User', roleId: 'r2' }],
};

const ACCOUNTS = [{ label: 'Alpha Fleet', value: 7 }];

// A probe rather than renderHook: @testing-library/react-hooks is not installed.
// Same approach as useUsers.test.js.
let hook;

function Probe({ isOpen }) {
  hook = useFormOptions(isOpen);

  return (
    <div>
      <span data-testid="job-roles">{hook.options.jobRoles.length}</span>
      <span data-testid="accounts">
        {hook.accountOptions.map((account) => account.label).join(',')}
      </span>
    </div>
  );
}

const at = (id) => screen.getByTestId(id).textContent;

const respondOk = (accounts = ACCOUNTS, total = accounts.length) => {
  global.fetch = jest.fn().mockImplementation((url) =>
    Promise.resolve({
      ok: true,
      json: async () =>
        String(url).includes('admin_accounts')
          ? { accounts, total }
          : FORM_OPTIONS,
    }),
  );
};

/**
 * Answers each admin_accounts request from `pages`, keyed by the offset asked for.
 *
 * Lets a test assert what appending actually does rather than stubbing one reply
 * for every page.
 */
const respondWithPages = (pages, total) => {
  global.fetch = jest.fn().mockImplementation((url) => {
    const path = String(url);

    if (!path.includes('admin_accounts')) {
      return Promise.resolve({ ok: true, json: async () => FORM_OPTIONS });
    }

    const match = path.match(/offset=(\d+)/);
    const offset = match ? Number(match[1]) : 0;

    return Promise.resolve({
      ok: true,
      json: async () => ({ accounts: pages[offset] || [], total }),
    });
  });
};

beforeEach(() => jest.useFakeTimers());

afterEach(() => {
  jest.useRealTimers();
  delete global.fetch;
  hook = undefined;
});

describe('management/Users useFormOptions', () => {
  // The wizard mounts with the page, so fetching on mount would cost every
  // visitor a request they may never need.
  it('fetches nothing while closed', () => {
    respondOk();

    render(<Probe isOpen={false} />);

    expect(global.fetch).not.toHaveBeenCalled();
  });

  it('loads the options and accounts when it opens', async () => {
    respondOk();

    render(<Probe isOpen />);

    await waitFor(() => expect(at('job-roles')).toEqual('1'));
    expect(at('accounts')).toEqual('Alpha Fleet');
  });

  it('does not refetch when it opens again', async () => {
    respondOk();

    const { rerender } = render(<Probe isOpen />);
    await waitFor(() => expect(at('job-roles')).toEqual('1'));

    const calls = global.fetch.mock.calls.length;
    rerender(<Probe isOpen={false} />);
    rerender(<Probe isOpen />);

    expect(global.fetch.mock.calls.length).toEqual(calls);
  });

  // A failed option list leaves the selects empty rather than breaking the wizard.
  it('survives a failed load', async () => {
    global.fetch = jest.fn().mockRejectedValue(new Error('offline'));

    render(<Probe isOpen />);

    await waitFor(() => expect(global.fetch).toHaveBeenCalled());
    expect(at('job-roles')).toEqual('0');
  });

  it('allows a retry after a failed load', async () => {
    global.fetch = jest.fn().mockRejectedValue(new Error('offline'));

    const { rerender } = render(<Probe isOpen />);
    await waitFor(() => expect(global.fetch).toHaveBeenCalled());

    respondOk();
    rerender(<Probe isOpen={false} />);
    rerender(<Probe isOpen />);

    await waitFor(() => expect(at('job-roles')).toEqual('1'));
  });

  describe('searching accounts', () => {
    it('narrows the list', async () => {
      respondOk();
      render(<Probe isOpen />);
      await waitFor(() => expect(at('job-roles')).toEqual('1'));

      respondOk([{ label: 'Beta Fleet', value: 8 }]);
      hook.searchAccounts('beta');
      jest.runOnlyPendingTimers();

      await waitFor(() => expect(at('accounts')).toEqual('Beta Fleet'));
    });

    // SearchInput fires per keystroke, so only the last term should be requested.
    it('debounces, issuing one request for a run of keystrokes', async () => {
      respondOk();
      render(<Probe isOpen />);
      await waitFor(() => expect(at('job-roles')).toEqual('1'));

      const before = global.fetch.mock.calls.length;
      hook.searchAccounts('b');
      hook.searchAccounts('be');
      hook.searchAccounts('bet');
      jest.runOnlyPendingTimers();

      await waitFor(() =>
        expect(global.fetch.mock.calls.length).toEqual(before + 1),
      );
      expect(global.fetch.mock.calls[before][0]).toContain('q=bet');
    });

    it('empties the list when a search fails', async () => {
      respondOk();
      render(<Probe isOpen />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha Fleet'));

      global.fetch = jest.fn().mockRejectedValue(new Error('offline'));
      hook.searchAccounts('beta');
      jest.runOnlyPendingTimers();

      await waitFor(() => expect(at('accounts')).toEqual(''));
    });

    it('goes back to the first page for a new search', async () => {
      respondWithPages({ 0: [{ label: 'A', value: 1 }] }, 9);
      render(<Probe isOpen />);
      await waitFor(() => expect(at('accounts')).toEqual('A'));

      respondOk([{ label: 'Beta Fleet', value: 8 }], 1);
      hook.searchAccounts('beta');
      jest.runOnlyPendingTimers();

      await waitFor(() => expect(at('accounts')).toEqual('Beta Fleet'));
      expect(global.fetch.mock.calls[0][0]).not.toContain('offset');
    });
  });

  // An OEM admin can administer thousands, so the menu holds a page at a time.
  describe('paging accounts', () => {
    const PAGE_ONE = [{ label: 'A', value: 1 }];
    const PAGE_TWO = [{ label: 'B', value: 2 }];

    const openWithPages = async (pages, total) => {
      respondWithPages(pages, total);
      render(<Probe isOpen />);
      await waitFor(() => expect(at('job-roles')).toEqual('1'));
    };

    it('reports the total and that more remain', async () => {
      await openWithPages({ 0: PAGE_ONE }, 3);

      expect(hook.accountsTotal).toEqual(3);
      expect(hook.hasMoreAccounts).toBe(true);
    });

    it('reports nothing more once the page is the whole list', async () => {
      await openWithPages({ 0: PAGE_ONE }, 1);

      expect(hook.hasMoreAccounts).toBe(false);
    });

    it('appends the next page rather than replacing it', async () => {
      await openWithPages({ 0: PAGE_ONE, 1: PAGE_TWO }, 2);

      hook.loadMoreAccounts();

      await waitFor(() => expect(at('accounts')).toEqual('A,B'));
    });

    it('asks for the next page at the offset it has reached', async () => {
      await openWithPages({ 0: PAGE_ONE, 1: PAGE_TWO }, 2);
      const before = global.fetch.mock.calls.length;

      hook.loadMoreAccounts();

      await waitFor(() =>
        expect(global.fetch.mock.calls.length).toBeGreaterThan(before),
      );
      expect(global.fetch.mock.calls[before][0]).toContain('offset=1');
    });

    it('stops asking once everything is loaded', async () => {
      await openWithPages({ 0: PAGE_ONE }, 1);
      const before = global.fetch.mock.calls.length;

      hook.loadMoreAccounts();

      expect(global.fetch.mock.calls.length).toEqual(before);
    });

    // A page can overlap the one before it when accounts are created between the
    // two requests, and a repeated row would collide on OptionsList's keys.
    it('drops an account the previous page already carried', async () => {
      await openWithPages({ 0: PAGE_ONE, 1: [...PAGE_ONE, ...PAGE_TWO] }, 3);

      hook.loadMoreAccounts();

      await waitFor(() => expect(at('accounts')).toEqual('A,B'));
    });

    // Losing a page must not empty the menu under the admin: the accounts already
    // on screen are still theirs to pick.
    it('keeps what is loaded when the next page fails', async () => {
      await openWithPages({ 0: PAGE_ONE }, 3);

      global.fetch = jest.fn().mockRejectedValue(new Error('offline'));
      hook.loadMoreAccounts();

      await waitFor(() => expect(global.fetch).toHaveBeenCalled());
      expect(at('accounts')).toEqual('A');
    });
  });
});
